import React, { useEffect, useState } from "react";
import MtiButton from "../Buttons/MtiButton";
import "../../WarningView.css";
import Modal from "../Common/Modal";
import PlateTypeApi from "../../api/PlateTypeApi";
import "./RestorePlateTypeModalView.css";
import PlateTypeBackupTable from "./PlateTypeBackupTable";
import { PlateTypeBackupEntry } from "../../Model/Plate";
import CommonTable from "../Common/CommonTable";
import { Column } from "react-table";
import DateUtil from "../../Utility/DateUtil";
import { useDispatch } from "react-redux";
import { sendNotification } from "../../redux/actions/NotificationAction";
import { ENotificationType } from "../../Model/ENotificationType";

type Props = {
  showRestorePlateType: boolean;
  setShowRestorePlateType: (show: boolean) => void;
  selectedPlateTypeId: number;
  selectedImagerIdentifier: string;
  selectedPlateTypeBackupEntry: PlateTypeBackupEntry | null;
  setSelectedPlateTypeBackupEntry: (entry: PlateTypeBackupEntry | null) => void;
};

export default function RestorePlateTypeModalView({
  showRestorePlateType,
  setShowRestorePlateType,
  selectedPlateTypeBackupEntry,
  setSelectedPlateTypeBackupEntry,
  selectedPlateTypeId,
  selectedImagerIdentifier,
}: Props) {
  const [data, setData] = useState([] as PlateTypeBackupEntry[]);
  const [showWarning, setShowWarning] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!showWarning) {
      updateData();
    }
  }, [selectedPlateTypeId, showRestorePlateType]);

  const updateData = () => {
    const endDate = new Date();
    const startDate = new Date(2000, 1);

    PlateTypeApi.getPlateTypeBackupEntries(
      selectedPlateTypeId,
      startDate,
      endDate
    ).then((value) => setData(value));
  };

  const columns: Column<PlateTypeBackupEntry>[] = React.useMemo(
    () => [
      {
        Header: "Date",
        id: "Date",
        accessor: (row: PlateTypeBackupEntry) =>
          DateUtil.toUsFormatString(DateUtil.toDate(row.date)),
        width: "20%",
      },
      {
        Header: "Plate Type",
        id: "Name",
        accessor: (row: PlateTypeBackupEntry) => row.name,
        width: "30%",
      },
      {
        Header: "Note",
        id: "Note",
        accessor: (row: PlateTypeBackupEntry) => row.note,
        width: "50%",
      },
    ],
    []
  );

  return (
    <div>
      <Modal
        show={showRestorePlateType}
        hideOnBackdropClick={false}
        className="restore-plate-type-modal-view"
      >
        <Modal.Header
          title="Restore Plate Type Definition"
          hideButtonEnabled={false}
          className="restore-plate-type-modal-view-header"
        />
        <Modal.Body className="restore-plate-type-modal-view-content">
          <CommonTable<PlateTypeBackupEntry>
            setSelectedRows={(rows) => setSelectedPlateTypeBackupEntry(rows[0])}
            data={data}
            columns={columns}
            enableMultiSelect={false}
            hidePaginationIfNotNeeded={false}
            enableFilter={false}
            variant={"no-separator"}
            pageLineSize={5}
          />
        </Modal.Body>
        <Modal.Footer className="restore-plate-type-modal-view-footer">
          <MtiButton
            disabled={selectedPlateTypeBackupEntry == null}
            size="large"
            onClick={() => {
              setShowWarning(true);
              setShowRestorePlateType(false);
            }}
          >
            Recover
          </MtiButton>
          <MtiButton
            size="large"
            variant="secondary"
            style={{
              marginRight: 10,
            }}
            onClick={() => {
              setShowRestorePlateType(false);
              setSelectedPlateTypeBackupEntry(null);
            }}
          >
            Cancel
          </MtiButton>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showWarning}
        hideOnBackdropClick={false}
        className="restore-plate-type-modal-view"
        style={{
          width: "700px",
          height: "400px",
        }}
      >
        <Modal.Header
          title="Restore Plate Type Definition"
          hideButtonEnabled={false}
          className="restore-plate-type-modal-view-header"
        />
        <Modal.Body className="restore-plate-type-modal-view-content">
          <div className="restore-plate-type-modal-view-content-warning">
            <svg
              width="84"
              height="84"
              viewBox="0 0 84 84"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M41.9998 76.1709C61.0997 76.1709 76.5832 60.6875 76.5832 41.5876C76.5832 22.4878 61.0997 7.00427 41.9998 7.00427C22.9 7.00427 7.4165 22.4878 7.4165 41.5876C7.4165 60.6875 22.9 76.1709 41.9998 76.1709ZM35.0832 13.9209L38.5415 48.5043H45.4582L48.9165 13.9209H35.0832ZM41.9998 69.2543C45.8198 69.2543 48.9165 66.1576 48.9165 62.3376C48.9165 58.5176 45.8198 55.4209 41.9998 55.4209C38.1799 55.4209 35.0832 58.5176 35.0832 62.3376C35.0832 66.1576 38.1799 69.2543 41.9998 69.2543Z"
                fill="#F7A246"
              />
            </svg>
            <label className="restore-plate-type-modal-view-content-warning-first-label">
              Are you sure to restore {selectedPlateTypeBackupEntry?.name}?
            </label>
            <label className="restore-plate-type-modal-view-content-warning-second-label">
              The changes will overide your current setting
            </label>
          </div>
        </Modal.Body>
        <Modal.Footer className="restore-plate-type-modal-view-footer">
          <MtiButton
            disabled={selectedPlateTypeBackupEntry == null}
            size="large"
            onClick={() => {
              if (selectedPlateTypeBackupEntry != null)
                PlateTypeApi.restorePlateType(
                  selectedPlateTypeBackupEntry.id,
                  selectedPlateTypeId,
                  selectedImagerIdentifier
                )
                  .then(() => {
                    dispatch(
                      sendNotification(
                        ENotificationType.Success,
                        "The changes have been saved successfully"
                      )
                    );
                  })
                  .catch((reason) => {
                    sendNotification(ENotificationType.Error, reason);
                  });
              setShowRestorePlateType(false);
              setSelectedPlateTypeBackupEntry(null);
              setShowWarning(false);
            }}
          >
            Recover
          </MtiButton>
          <MtiButton
            size="large"
            variant="secondary"
            style={{
              marginRight: 10,
            }}
            onClick={() => {
              setShowRestorePlateType(false);
              setSelectedPlateTypeBackupEntry(null);
              setShowWarning(false);
            }}
          >
            Cancel
          </MtiButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
